import { useState, useEffect, useMemo, useContext } from 'react';
import apiUser from '../../apiUser';
import { UserContext } from '../../contexts/userProvider';
import DataTable from '../Table';
import LoadingSpinner from '../Loader/load';
import Modal from './Modal';
import HistoricoCliente from './HistoricoCliente';
import TimelineCiclo from './TimelineCiclo';
import CockpitCiclo from './cockpit/CockpitCiclo';
import AjustesRetroativos from './AjustesRetroativos';
import EstornosPendentes from './EstornosPendentes';
import {
    fmtBRL, fmtComp, fmtDataHora, avisoDe, compParam,
    STATUS_META, STATUS_CARDS, situacaoChave, StatusBadge, DivergenciaBadge, acaoSugerida, UFS, nomeContrato,
} from './format';

const Painel = () => {
    const { setAlert, isAdmin } = useContext(UserContext);
    const [painel, setPainel] = useState({ ciclos: [], contadores: {}, valores: {}, cobertura: null });
    const [loading, setLoading] = useState(false);
    const [filtroSit, setFiltroSit] = useState('atencao'); // 'atencao' | 'todos' | <status>; select + cards
    const [competencias, setCompetencias] = useState([]);
    const [filtroComp, setFiltroComp] = useState('');
    const [estado, setEstado] = useState('');             // '' | RS | SC | PR | outros
    const [busca, setBusca] = useState('');
    const [config, setConfig] = useState(null);
    const [detalhe, setDetalhe] = useState(null);
    const [confirmaDisparo, setConfirmaDisparo] = useState(false);
    const [compDisparo, setCompDisparo] = useState('');
    const [modo, setModo] = useState('acoes');             // 'acoes' | 'contestando' | 'historico'
    const [formCont, setFormCont] = useState({ nome: '', email: '', mensagem: '' });
    const [contestacao, setContestacao] = useState(null);  // contestação do ciclo aberto
    const [anotacao, setAnotacao] = useState('');
    const [comentariosCiclo, setComentariosCiclo] = useState(null); // ciclo com o modal de comentários aberto

    const aviso = avisoDe(setAlert);

    const acao = async (rota, payload) => {
        try {
            const { data } = await apiUser.post(rota, payload);
            aviso(data.message, data.success ? 'success' : 'warning');
            if (data.success) carregar();
            return data.success;
        } catch { aviso('Erro ao comunicar com o servidor.'); return false; }
    };

    const carregarEstaticos = async () => {
        try {
            const [c, comp] = await Promise.all([
                apiUser.get('/faturamento/config'),
                apiUser.get('/faturamento/competencias'),
            ]);
            if (c.data.success) setConfig(c.data.dados);
            if (comp.data.success) setCompetencias(comp.data.dados || []);
        } catch { aviso('Erro ao carregar o painel.'); }
    };

    const carregar = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filtroComp) params.competencia = filtroComp;
            if (estado) params.estado = estado;
            if (filtroSit === 'faturado_aprovado') { params.status = 'faturado'; params.faturado_auto = '0'; }
            else if (filtroSit === 'faturado_auto') { params.status = 'faturado'; params.faturado_auto = '1'; }
            else if (filtroSit === 'atencao') params.atencao = '1';
            else if (filtroSit && filtroSit !== 'todos') params.status = filtroSit;
            const p = await apiUser.get('/faturamento/painel', { params });
            if (p.data.success) setPainel(p.data.dados);
        } catch { aviso('Erro ao carregar o painel.'); }
        setLoading(false);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { carregarEstaticos(); }, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { carregar(); }, [filtroSit, filtroComp, estado]);

    const disparar = async () => {
        setConfirmaDisparo(false); setCompDisparo('');
        try {
            const { data } = await apiUser.post('/faturamento/disparar', { competencia: compParam(compDisparo) });
            aviso(data.message, data.success ? 'success' : 'warning');
            if (data.success) setTimeout(() => { carregar(); carregarEstaticos(); }, 4000);
        } catch { aviso('Erro ao comunicar com o servidor.'); }
    };

    const fecharDetalhe = () => {
        setDetalhe(null); setModo('acoes'); setFormCont({ nome: '', email: '', mensagem: '' });
        setContestacao(null); setAnotacao('');
    };

    // Fecha o modal de comentários e recarrega o painel — o usuário pode ter comentado
    // dentro do modal, e a linha da tabela precisa refletir o novo qtd_comentarios.
    const fecharComentarios = () => { setComentariosCiclo(null); carregar(); };

    const verDetalhe = async (id) => {
        const ciclo = painel.ciclos.find((x) => String(x.id) === String(id));
        if (!ciclo) return;
        setDetalhe(ciclo); setModo('acoes'); setFormCont({ nome: '', email: '', mensagem: '' });
        setContestacao(null); setAnotacao('');
        if (ciclo.status === 'contestado') {
            try {
                const { data } = await apiUser.get('/faturamento/ciclo/contestacao', { params: { id_ciclo: ciclo.id } });
                if (data.success) { setContestacao(data.dados || {}); setAnotacao(data.dados?.resposta_interna || ''); }
            } catch { /* silencioso: o detalhe ainda abre */ }
        }
    };

    // Leva 2B: /ciclo/enviar agora exige id_contrato (Task 7). GET /faturamento/painel
    // não devolve esse campo por linha (só contrato_nome/contrato_numero) — gap de
    // backend, não corrigido aqui por instrução explícita da Task 9; ver task-9-report.md.
    const reenviarMesmo = (ciclo) => acao('/faturamento/ciclo/enviar', {
        id_contrato: ciclo.id_contrato, competencia: String(ciclo.competencia).slice(0, 10),
    });
    const recalcularReenviar = (id) => acao('/faturamento/ciclo/atualizarReenviar', { id_ciclo: id });
    const faturar = (id) => acao('/faturamento/ciclo/faturar', { id_ciclo: id });

    const contestarManual = async () => {
        if (!formCont.mensagem.trim()) { aviso('Descreva o motivo da contestação.'); return; }
        const ok = await acao('/faturamento/ciclo/contestarManual', { id_ciclo: detalhe.id, ...formCont });
        if (ok) fecharDetalhe();
    };

    const salvarAnotacao = async () => {
        if (!contestacao?.id) return;
        try {
            const { data } = await apiUser.post('/faturamento/contestacoes/atualizar', {
                id: contestacao.id, resposta_interna: anotacao,
            });
            aviso(data.message, data.success ? 'success' : 'warning');
        } catch { aviso('Erro ao comunicar com o servidor.'); }
    };

    const headers = [
        { label: 'Cliente', key: 'cliente_nome', sortable: true, wrapText: true },
        {
            label: 'Contrato', key: 'contrato_nome', sortable: true, wrapText: true,
            render: (item) => item.contrato_nome ? nomeContrato(item) : '—',
        },
        { label: 'Competência', key: 'competencia_fmt', sortable: true, width: 'w-32' },
        { label: 'Valor', key: 'valor_total', sortable: false, type: 'money', width: 'w-36' },
        {
            label: 'Situação', key: 'situacao_label', sortable: false, width: 'w-40',
            render: (item) => (
                <span className='inline-flex items-center gap-1 flex-wrap'>
                    <StatusBadge ciclo={item} />
                    <DivergenciaBadge ciclo={item} compact />
                </span>
            ),
        },
        { label: 'Prazo', key: 'prazo_fmt', sortable: false, width: 'w-44' },
        {
            label: 'Ações', key: 'actions', sortable: false, width: 'w-48',
            render: (item) => {
                const a = acaoSugerida(item);
                return (
                    <div className='inline-flex items-center gap-1.5'>
                        <button onClick={() => verDetalhe(item.id)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${a.primaria
                                ? 'bg-vox-blue-600 hover:bg-vox-blue-700 text-white'
                                : 'bg-gray-100 hover:bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-100'}`}>
                            {a.label}
                        </button>
                        <button onClick={() => setComentariosCiclo(item)} title='Comentários'
                            className='relative px-2 py-1.5 rounded-lg text-sm bg-gray-100 hover:bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-100'>
                            💬
                            {item.qtd_comentarios > 0 && (
                                <span className='absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-vox-blue-600 text-white text-[10px] font-bold flex items-center justify-center'>
                                    {item.qtd_comentarios}
                                </span>
                            )}
                        </button>
                    </div>
                );
            },
        },
    ];

    const data = useMemo(() => painel.ciclos.map((c) => ({
        ...c,
        competencia_fmt: fmtComp(c.competencia),
        situacao_label: STATUS_META[situacaoChave(c)]?.label || c.status,
        prazo_fmt: fmtDataHora(c.prazo_em),
    })), [painel.ciclos]);

    const buscaLimpa = busca.trim().toLowerCase();
    const linhas = useMemo(() => (buscaLimpa
        ? data.filter((d) => (d.cliente_nome || '').toLowerCase().includes(buscaLimpa))
        : data), [data, buscaLimpa]);

    const totalCiclos = Object.values(painel.contadores).reduce((a, b) => a + b, 0);
    const atencaoLimpa = filtroSit === 'atencao';
    const semFiltro = !estado && !filtroComp;

    const fraseStatus = config?.envio_ativo === '1'
        ? `✅ Disparo automático: LIGADO · próximo envio dia ${config.envio_dia} · ${config.faturar_auto === '1'
            ? `sem resposta em ${config.prazo_horas}h, fatura sozinho`
            : `sem resposta em ${config.prazo_horas}h, aguarda você faturar (não fatura sozinho)`}`
        : '⏸ Disparo automático: DESLIGADO · os envios são manuais';

    return (
        <div className='w-full'>
            <div className='mb-4 flex flex-col gap-2'>
                <div className={`flex items-center justify-between gap-3 flex-wrap px-4 py-2 rounded-lg text-sm font-medium ${config?.envio_ativo === '1' ? 'bg-green-50 text-green-900 border border-green-200' : 'bg-gray-100 text-gray-700 border border-gray-200'}`}>
                    <span>{fraseStatus}</span>
                    {isAdmin && !confirmaDisparo && (
                        <button onClick={() => setConfirmaDisparo(true)} className='px-4 py-2 rounded-lg text-white font-semibold shrink-0' style={{ backgroundColor: '#9ac31c' }}>Enviar para todos os clientes</button>
                    )}
                </div>
                {isAdmin && confirmaDisparo && (
                    <div className='flex flex-col gap-2 bg-yellow-50 border border-yellow-300 rounded-lg p-3'>
                        <div className='flex items-center gap-2 flex-wrap'>
                            <span className='text-sm text-yellow-900 font-semibold'>Competência:</span>
                            <input type='month' value={compDisparo} onChange={(e) => setCompDisparo(e.target.value)}
                                className='border rounded-lg p-1.5 text-sm dark:bg-gray-700 dark:text-white' />
                            <span className='text-xs text-gray-500'>vazio = mês anterior</span>
                        </div>
                        <span className='text-sm text-yellow-900'>Enviar o relatório de {compDisparo || 'mês anterior'} a todos os clientes habilitados?</span>
                        <div className='flex items-center gap-2'>
                            <button onClick={disparar} className='px-3 py-1.5 rounded-lg text-white font-semibold' style={{ backgroundColor: '#9ac31c' }}>Confirmar envio</button>
                            <button onClick={() => { setConfirmaDisparo(false); setCompDisparo(''); }} className='px-3 py-1.5 bg-gray-200 rounded-lg'>Cancelar</button>
                        </div>
                    </div>
                )}
            </div>

            <div className='grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-7 gap-3 mb-5'>
                {STATUS_CARDS.map((k) => {
                    const m = STATUS_META[k];
                    return (
                        <button key={k} onClick={() => setFiltroSit(filtroSit === k ? 'atencao' : k)}
                            title={filtroSit === k ? 'Remover filtro' : `Filtrar por ${m.label}`}
                            className={`rounded-xl px-3 py-4 text-center border border-transparent transition-all duration-150 hover:shadow-md ${filtroSit === k ? 'ring-2 ring-vox-blue-600 shadow-md' : ''} ${m.cls}`}>
                            <div className='text-2xl font-bold'>{painel.contadores[k] || 0}</div>
                            <div className='text-[11px] font-semibold opacity-80'>{fmtBRL(painel.valores?.[k] || 0)}</div>
                            <div className='text-xs font-semibold'>{m.label}</div>
                        </button>
                    );
                })}
            </div>

            <AjustesRetroativos aviso={aviso} onMudou={carregar} />
            <EstornosPendentes aviso={aviso} onMudou={carregar} />

            {filtroComp && painel.cobertura?.semCiclo?.length > 0 && (
                <div className='mb-4 p-3 rounded-lg bg-yellow-50 border border-yellow-300 text-yellow-900 text-sm'>
                    ⚠ {painel.cobertura.semCiclo.length} de {painel.cobertura.habilitados} cliente(s) habilitado(s) sem faturamento nesta competência:{' '}
                    {painel.cobertura.semCiclo.slice(0, 5).join(', ')}{painel.cobertura.semCiclo.length > 5 ? ` +${painel.cobertura.semCiclo.length - 5}` : ''}
                </div>
            )}

            <div className='flex items-end justify-between gap-3 mb-5 flex-wrap'>
                <div className='flex items-end gap-3 flex-wrap'>
                    <div>
                        <label className='block text-xs text-gray-600 dark:text-gray-300 mb-1'>Situação</label>
                        <select value={filtroSit} onChange={(e) => setFiltroSit(e.target.value)}
                            className='border rounded-lg p-2 dark:bg-gray-700 dark:text-white'>
                            <option value='atencao'>Precisa de atenção</option>
                            <option value='todos'>Todos</option>
                            {STATUS_CARDS.map((k) => <option key={k} value={k}>{STATUS_META[k].label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className='block text-xs text-gray-600 dark:text-gray-300 mb-1'>Estado</label>
                        <select value={estado} onChange={(e) => setEstado(e.target.value)}
                            className='border rounded-lg p-2 dark:bg-gray-700 dark:text-white'>
                            <option value=''>Todos</option>
                            {UFS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className='block text-xs text-gray-600 dark:text-gray-300 mb-1' title='mês de referência'>Competência</label>
                        <select value={filtroComp} onChange={(e) => setFiltroComp(e.target.value)}
                            className='border rounded-lg p-2 dark:bg-gray-700 dark:text-white'>
                            <option value=''>Todas</option>
                            {competencias.map((c) => <option key={c} value={c}>{fmtComp(c)}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className='block text-xs text-gray-600 dark:text-gray-300 mb-1'>Buscar cliente</label>
                        <div className='relative'>
                            <input type='text' value={busca} onChange={(e) => setBusca(e.target.value)}
                                placeholder='Nome do cliente'
                                className='border rounded-lg p-2 pr-8 w-56 dark:bg-gray-700 dark:text-white' />
                            {busca && (
                                <button onClick={() => setBusca('')} title='Limpar busca'
                                    className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'>×</button>
                            )}
                        </div>
                    </div>
                </div>
                <div className='flex items-center gap-3'>
                    <button onClick={() => { carregar(); carregarEstaticos(); }} className='px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-white rounded-lg'>Atualizar</button>
                </div>
            </div>

            {loading ? <LoadingSpinner /> : painel.ciclos.length === 0 ? (
                totalCiclos === 0 && semFiltro ? (
                    <div className='p-8 text-center text-gray-600 dark:text-gray-300'>
                        {isAdmin ? (
                            <p className='mb-3'>Nenhum faturamento ainda. Comece em <b>Destinatários</b>: ligue o envio de um cliente e cadastre o e-mail dele.</p>
                        ) : (
                            <p>Nenhum faturamento ainda. Peça ao gestor para habilitar os clientes e cadastrar os e-mails.</p>
                        )}
                    </div>
                ) : atencaoLimpa ? (
                    <div className='p-8 text-center text-gray-600 dark:text-gray-300'>✅ Tudo em dia — nada precisa de você agora.</div>
                ) : (
                    <div className='p-8 text-center text-gray-500'>Nenhum faturamento neste filtro.</div>
                )
            ) : linhas.length === 0 ? (
                <div className='p-8 text-center text-gray-500'>Nenhum cliente com “{busca.trim()}” nesta lista.</div>
            ) : (
                <DataTable headers={headers} data={linhas} perPageItems={15} autoWidth />
            )}

            {detalhe && (
                <Modal
                    maxW={modo === 'historico' ? 'max-w-5xl' : 'max-w-2xl'}
                    title={`${detalhe.cliente_nome} — ${fmtComp(detalhe.competencia)}`}
                    subtitle={
                        <>
                            <p>
                                <StatusBadge ciclo={detalhe} compact /> • valor {fmtBRL(detalhe.valor_total)}
                                {detalhe.respondido_por ? ` • respondido por ${detalhe.respondido_por}` : ''}
                                {detalhe.faturado_auto === '1' ? ' • faturado pelo prazo' : ''}
                            </p>
                            {detalhe.erro_msg && <p className='text-sm text-red-600 mt-1'>{detalhe.erro_msg}</p>}
                        </>
                    }
                    onClose={fecharDetalhe}
                    footer={
                        <>
                            {modo === 'historico' ? (
                                <button onClick={() => setModo('acoes')} className='px-4 py-2 bg-gray-200 dark:bg-gray-600 dark:text-gray-200 rounded-lg'>Voltar</button>
                            ) : (
                                <button onClick={() => setModo('historico')} className='px-4 py-2 bg-vox-blue-600 hover:bg-vox-blue-700 text-white rounded-lg'>Histórico deste cliente</button>
                            )}
                            <button onClick={fecharDetalhe} className='px-4 py-2 bg-gray-200 dark:bg-gray-600 dark:text-gray-200 rounded-lg'>Fechar</button>
                        </>
                    }
                >
                    {modo === 'historico' ? (
                        <div className='px-6 py-5'>
                            <HistoricoCliente idCliente={detalhe.id_cliente} clienteNome={detalhe.cliente_nome} />
                        </div>
                    ) : modo === 'contestando' ? (
                        <div className='px-6 py-5 flex flex-col gap-3'>
                            <p className='text-sm font-semibold text-gray-700 dark:text-gray-300'>Registrar contestação manual</p>
                            <div>
                                <label className='block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1'>Nome do contato (opcional)</label>
                                <input type='text' value={formCont.nome} onChange={(e) => setFormCont({ ...formCont, nome: e.target.value })}
                                    className='border border-gray-300 dark:border-gray-600 rounded-lg p-2 w-full dark:bg-gray-700 dark:text-white' />
                            </div>
                            <div>
                                <label className='block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1'>E-mail do contato (opcional)</label>
                                <input type='email' value={formCont.email} onChange={(e) => setFormCont({ ...formCont, email: e.target.value })}
                                    className='border border-gray-300 dark:border-gray-600 rounded-lg p-2 w-full dark:bg-gray-700 dark:text-white' />
                            </div>
                            <div>
                                <label className='block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1'>Motivo <span className='text-red-500'>*</span></label>
                                <textarea value={formCont.mensagem} onChange={(e) => setFormCont({ ...formCont, mensagem: e.target.value })}
                                    placeholder='Ex.: cliente contestou por e-mail/telefone — descreva o motivo' rows={3}
                                    className='border border-gray-300 dark:border-gray-600 rounded-lg p-2 w-full dark:bg-gray-700 dark:text-white' />
                            </div>
                            <div className='flex gap-3'>
                                <button onClick={contestarManual} className='px-4 py-2 text-white rounded-lg' style={{ backgroundColor: '#f97316' }}>Confirmar</button>
                                <button onClick={() => setModo('acoes')} className='px-4 py-2 bg-gray-200 dark:bg-gray-600 dark:text-white rounded-lg'>Voltar</button>
                            </div>
                        </div>
                    ) : (
                        <CockpitCiclo
                            ciclo={detalhe}
                            isAdmin={isAdmin}
                            contestacao={contestacao}
                            anotacao={anotacao}
                            setAnotacao={setAnotacao}
                            salvarAnotacao={salvarAnotacao}
                            onRecalcular={async (id) => { const ok = await recalcularReenviar(id); if (ok) fecharDetalhe(); }}
                            onRecalculadoSalvo={carregar}
                            onReenviar={async (c) => { const ok = await reenviarMesmo(c); if (ok) fecharDetalhe(); }}
                            onFaturar={async (id) => { const ok = await faturar(id); if (ok) fecharDetalhe(); }}
                            onContestar={() => setModo('contestando')}
                        />
                    )}
                </Modal>
            )}

            {comentariosCiclo && (
                <Modal
                    title={`Comentários — ${comentariosCiclo.cliente_nome}${comentariosCiclo.contrato_nome ? ` (${nomeContrato(comentariosCiclo)})` : ''}`}
                    onClose={fecharComentarios}
                    footer={<button onClick={fecharComentarios} className='px-4 py-2 bg-gray-200 dark:bg-gray-600 dark:text-gray-200 rounded-lg'>Fechar</button>}
                >
                    <TimelineCiclo idCiclo={comentariosCiclo.id} />
                </Modal>
            )}
        </div>
    );
};

export default Painel;
